import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ChartData {
  name: string;
  value: number;
  [key: string]: any;
}

interface AnalysisQuota {
  used: number;
  remaining: number;
  limit: number;
}

export const useChartAnalysis = (chartType?: string, title?: string) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [insights, setInsights] = useState<string | null>(null);
  const [quota, setQuota] = useState<AnalysisQuota>({ used: 0, remaining: 5, limit: 5 });
  const [analysisAge, setAnalysisAge] = useState<number | null>(null);
  const [isCached, setIsCached] = useState(false);

  // Charger les insights existants au montage
  useEffect(() => {
    if (chartType && title) {
      loadExistingAnalysis(chartType, title);
    }
  }, [chartType, title]);

  const loadExistingAnalysis = async (type: string, chartTitle: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Chercher l'analyse la plus récente pour ce graphique
      const { data, error } = await supabase
        .from('ai_analyses')
        .select('insights, created_at')
        .eq('user_id', user.id)
        .eq('chart_type', type)
        .eq('chart_title', chartTitle)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!error && data) {
        setInsights(data.insights);
        const ageInDays = Math.floor(
          (Date.now() - new Date(data.created_at).getTime()) / (1000 * 60 * 60 * 24)
        );
        setAnalysisAge(ageInDays);
        setIsCached(true);
      }

      // Charger le quota actuel
      await loadQuota();
    } catch (error) {
      console.error('Error loading existing analysis:', error);
    }
  };

  const loadQuota = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('ai_usage_limits')
        .select('chart_analyses_count')
        .eq('user_id', user.id)
        .eq('usage_date', today)
        .maybeSingle();

      const used = data?.chart_analyses_count || 0;
      setQuota({ used, remaining: 5 - used, limit: 5 });
    } catch (error) {
      console.error('Error loading quota:', error);
    }
  };

  const analyzeChart = async (
    chartData: ChartData[],
    chartType: string,
    title: string,
    forceNew: boolean = false
  ) => {
    setIsAnalyzing(true);

    try {
      const { data, error } = await supabase.functions.invoke('manage-ai-analysis', {
        body: {
          chartData,
          chartType,
          title,
          forceNew,
        },
      });

      if (error) throw error;

      if (data?.error) {
        if (data.error === 'Quota quotidien dépassé') {
          toast.error(`Quota dépassé : ${data.quotaUsed}/${data.quotaLimit} analyses utilisées aujourd'hui`);
          setQuota({ used: data.quotaUsed, remaining: 0, limit: data.quotaLimit });
        } else {
          toast.error(data.error);
        }
        return;
      }

      if (data?.insights) {
        setInsights(data.insights);
        setIsCached(data.cached);
        setAnalysisAge(data.ageInDays);
        setQuota({
          used: data.quotaUsed,
          remaining: data.quotaRemaining,
          limit: data.quotaLimit ?? 5,
        });

        if (data.cached) {
          toast.success(`Analyse récupérée (${data.ageInDays} jour${data.ageInDays > 1 ? 's' : ''})`);
        } else {
          toast.success(`Analyse terminée (${data.quotaRemaining}/10 restantes)`);
        }
      }
    } catch (error) {
      console.error('Error analyzing chart:', error);
      toast.error('Erreur lors de l\'analyse IA');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearInsights = () => {
    setInsights(null);
    setAnalysisAge(null);
    setIsCached(false);
  };

  return {
    isAnalyzing,
    insights,
    analyzeChart,
    clearInsights,
    quota,
    analysisAge,
    isCached,
  };
};
